import React, { Component, MouseEvent } from 'react';
import axios from 'axios';
import Alert from '@material-ui/lab/Alert';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography, { TypographyProps } from '@material-ui/core/Typography';
import TableContainer from '@material-ui/core/TableContainer';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import TableCell, { TableCellProps } from '@material-ui/core/TableCell';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import TextField, { TextFieldProps } from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import {
  IRowDiversityState,
  IDiversitySubmitState,
  IDiversityProps,
  IDiversityState,
  IDiversityRow
} from '../../types/Diversity.types';
import { endpoints } from '../../config';
import { isValidFilename } from '../../utils/validations';
import ReactMarkDown from 'react-markdown';

const useStyles = makeStyles(theme =>
  createStyles({
    textField: {
      '& .MuiInput-underline:before, .MuiInput-underline:after, .MuiInput-underline:hover:not(.Mui-disabled):before': {
        borderBottom: 'none'
      },
      '&, & .MuiInputBase-root ': {
        maxWidth: 155,
        maxHeight: 46
      },
      '&. input::-webkit-calendar-picker-indicator': {
        cursor: 'pointer'
      },
      '& .Mui-disabled': {
        backgroundColor: theme.palette.grey[200],
        maxHeight: 46
      },
      '& .Mui-error': {
        color: theme.palette.error.main
      },
      '& .MuiOutlinedInput-input': {
        padding: 8
      },
      '& .MuiFormHelperText-root': {
        whiteSpace: 'nowrap',
        height: 0,
        marginBottom: 5
      }
    },
    textFieldFile: {
      '&': {
        maxWidth: 35,
        display: 'none',
        visibility: 'none'
      }
    },
    fileName: {
      '&': {
        maxWidth: 115,
        maxHeight: 46,
        display: 'block'
      },
      '& .MuiInputBase-input': {
        fontSize: 12
      }
    },
    tableCell: {
      '&': {
        padding: 12
      },
      '&.MuiTableCell-stickyHeader': {
        backgroundColor: '#ffffff'
      },
      '& i': {
        cursor: 'pointer'
      }
    },
    typography: {
      marginLeft: 70
    }
  })
);

export interface Column {
  id: string;
  label: string;
  align?: 'center' | 'inherit' | 'justify' | 'left' | 'right';
}

export const TextFieldStyled: React.FC<TextFieldProps> = (props: TextFieldProps) => {
  const classes = useStyles();
  return (
    <TextField
      {...props}
      className={
        props.type === 'file'
          ? classes.textFieldFile
          : props.id?.includes('fileName')
          ? classes.fileName
          : classes.textField
      }
    />
  );
};

export const TableCellStyled: React.FC<TableCellProps> = (props: TableCellProps) => {
  const classes = useStyles();
  return <TableCell {...props} className={classes.tableCell} />;
};

export const TypographyStyled: React.FC<TypographyProps & { isSecondLevel?: boolean }> = (
  props: TypographyProps & { isSecondLevel?: boolean }
) => {
  const { isSecondLevel, children, ...restProps } = props;
  const classes = useStyles();
  return (
    <Typography {...restProps} className={isSecondLevel ? classes.typography : ''}>
      {children}
    </Typography>
  );
};

class Diversity extends Component<IDiversityProps, IDiversityState> {
  state: Readonly<IDiversityState> = {
    diversityList: [],
    isModalOpened: false,
    isErrorDiversityList: false,
    errorDiversityList: this.props.languageStrings.diversity.errorDiversityList,
    loading: false,
    diversityInstructions: '',
    radioGroupValue: 'no'
  };

  componentDidUpdate = async () => {
    let isContinueEnabled = false;

    if(this.state.radioGroupValue === 'no') {
      isContinueEnabled = true;
    }
    
    const validDiversityExists = this.state.diversityList.some(
      diversity => diversity.fileName !== '' && diversity.isFilePushed !== true ? true : false);
    if (this.state.radioGroupValue === 'yes' && !validDiversityExists) {
        isContinueEnabled = true;
    }

    isContinueEnabled !== this.state.isContinueEnabled && this.setState({ isContinueEnabled });
  };

  componentDidMount = async () => {
    if (typeof this.props.cachedResponse !== 'undefined') {
      this.processDiversityList(this.props.cachedResponse);
    } else {
      this.setState({ loading: true });
      await this.getDiversityList();
      this.setState({ loading: false });
    }
    this.getDiversityInstructions();
  };

  processDiversityList = (list: any) => {
    const classList: IDiversityRow[] = [];
    const commonInitialState = {
      fileName: '',
      document: '',
      referenceId: '',
      startDate: '',
      expDate: '',
      isErrorFile: false,
      isErrorCertificate: false,
      isErrorStartDate: false,
      isErrorExpDate: false,
      isFilePushed: false
    };
    for (const element of list.data.response) {
      if (element.secondLevel.length > 0) {
        classList.push({
          ...commonInitialState,
          level1Id: element.id,
          level2Id: null,
          classification: element.l1divclassname,
          isExpanded: false,
          hasSecondLevel: true,
          ISCPUC: 'N'
        });
        for (const secondLevelItem of element.secondLevel) {
          classList.push({
            ...commonInitialState,
            level1Id: secondLevelItem.l1divclassid,
            level2Id: secondLevelItem.id,
            classification: secondLevelItem.l2divclassname,
            hasSecondLevel: false,
            ISCPUC: element.iscpuc
          });
        }
      } else {
        classList.push({
          ...commonInitialState,
          level1Id: element.id,
          level2Id: null,
          classification: element.l1divclassname,
          hasSecondLevel: false,
          ISCPUC: element.iscpuc
        });
      }
    }
    if (this.props.initialState.uploadCertificates.length > 0) {
      for (const rowItem of this.props.initialState.uploadCertificates) {
        let index = rowItem.index;
        if (typeof index !== 'number') {
          if (typeof rowItem.level2Id === 'number') {
            index = classList.findIndex(
              (item: IDiversityRow) => item.level1Id === rowItem.level1Id && item.level2Id === rowItem.level2Id
            );
          } else {
            index = classList.findIndex((item: IDiversityRow) => item.level1Id === rowItem.level1Id);
          }
        }
        classList[index] = {
          ...classList[index],
          level1Id: rowItem.level1Id,
          level2Id: rowItem.level2Id,
          classification: rowItem.classification,
          fileName: rowItem.originalFileName as string,
          document: rowItem.document,
          startDate: rowItem.startDate,
          expDate: rowItem.endDate,
          isErrorFile: rowItem.isErrorFile,
          isErrorCertificate: rowItem.isErrorCertificate,
          isErrorStartDate: rowItem.isErrorStartDate,
          isErrorExpDate: rowItem.isErrorExpDate,
          errorFile: rowItem.errorFile,
          errorCertificate: rowItem.errorCertificate,
          errorStartDate: rowItem.errorStartDate,
          errorExpDate: rowItem.errorExpDate,
          ISCPUC: rowItem.ISCPUC,
          referenceId: rowItem.referenceId,
          signedUrl: rowItem.signedUrl ? rowItem.signedUrl: '',
          isFilePushed: true
        };
      }
    }
    this.setState({
      diversityList: classList,
      radioGroupValue: this.props.initialState.radioGroupValue as string,
      isContinueEnabled: this.props.initialState.isContinueEnabled
    });
  };

  getDiversityInstructions = async () => {
    const instructions = await axios(`${endpoints.Diversity}/instructions?companyKey=${this.props.companyKey}`);
    if (instructions.status === 200) {     
      const { label } = instructions.data.response;
      if (label) {
          this.setState({ diversityInstructions: label })          
      }
    }
  };

  getDiversityList = async () => {
    try {
      const list = await axios(`${endpoints.Diversity}?companyKey=${this.props.companyKey}`);
      this.processDiversityList(list);
    } catch (error) {
      this.setState({ isErrorDiversityList: true });
    }
  };

  getSignedUrl = async (item: IDiversityRow, index: number, action?: string) => {
    const body = {
      companyKey: this.props.companyKey,
      level1Id: item.level1Id,
      level2Id: item.level2Id ?? 0,
      originalFileName: item.fileName,
      startDate: item.startDate,
      endDate: item.expDate,
      referenceId: item.referenceId && item.referenceId.length > 0 ? item.referenceId : null
    };
    try {
      if (item && action === 'delete') {
        await this.deleteFile(item.signedUrl as string);
      } else if (action === 'put') {
        const url = await axios.post(`${endpoints.Diversity}/uploadTmpDiversityFile`, body);
        await this.putFile(url.data.response.signedUrl, index, item);
      }
    } catch (error) {
      this.clearInputs(index, undefined, {
        isErrorFile: true,
        errorFile: this.props.languageStrings.diversity.errorDiversityList,
        isFilePushed: false
      });
      console.error('error getting signed url', error);
    }
  };

  putFile = async (signedUrl: string, index: number, item: IDiversityRow) => {
    try {      
      const config = {
        headers: {
          'x-amz-acl': 'bucket-owner-full-control',
          'Content-Type': 'application/octet-stream'
        }
      };
      if (!item.isFilePushed) {
        await axios.put(signedUrl, item.file, config);
        this.setState(({ diversityList }) => ({
          diversityList: [
            ...diversityList.slice(0, index),
            {
              ...diversityList[index],
              signedUrl,
              isFilePushed: true
            },
            ...diversityList.slice(index + 1)
          ]
        }));
      }
    } catch (error) {
      this.clearInputs(index, undefined, {
        isErrorFile: true,
        errorFile: this.props.languageStrings.diversity.errorUploadingFile,
        isFilePushed: false
      });
    }
  };

  deleteFile = async (signedUrl: string) => {
    try {
      const config = {
        headers: {
          'x-amz-acl': 'bucket-owner-full-control'
        }
      };
      await axios.delete(signedUrl as string, config);
    } catch (error) {
      console.error('error deleting file', error);
    }
  };

  handleFileSubmit = (index: number, action?: string) => {
    (this.state.diversityList[index].ISCPUC === 'Y' || !this.state.diversityList[index].isErrorCertificate) &&
      !this.state.diversityList[index].isErrorStartDate &&
      !this.state.diversityList[index].isErrorExpDate &&
      this.state.diversityList[index].file &&
      this.state.diversityList[index].startDate &&
      this.state.diversityList[index].expDate &&
      this.getSignedUrl(this.state.diversityList[index], index, action);
  };

  clearInputs = (index: number, callback?: Function, complementObject?: any) => {
    this.setState(
      ({ diversityList }) => ({
        diversityList: [
          ...diversityList.slice(0, index),
          {
            ...diversityList[index],
            ...complementObject,
            file: undefined,
            fileName: '',
            document: '',
            startDate: '',
            expDate: '',
            isErrorCertificate: false,
            isErrorStartDate: false,
            isErrorExpDate: false,
            referenceId: ''
          },
          ...diversityList.slice(index + 1)
        ],
        isModalOpened: false
      }),
      () => callback && callback(index)
    );
    const id = this.state.diversityList[index].level2Id || this.state.diversityList[index].level1Id;
    const fileInput: any = document.querySelector(`#file${id}`);
    fileInput.value = null;
    const certificateInput: any = document.querySelector(`#certificate${id}`);
    certificateInput.value = '';
  };

  setErrors = (index: number) => {
    if (this.state.diversityList[index].fileName || this.state.diversityList[index].referenceId) {
      this.setState(
        ({ diversityList }) => ({
          diversityList: [
            ...diversityList.slice(0, index),
            {
              ...diversityList[index],
              isErrorFile: diversityList[index].fileName === '' && diversityList[index].referenceId !== '' && true,
              errorFile: this.props.languageStrings.diversity.errorRequiredField,
              isErrorCertificate:
                diversityList[index].referenceId === '' && diversityList[index].ISCPUC === 'Y' && true,
              errorCertificate: this.props.languageStrings.diversity.errorRequiredField,
              isErrorStartDate:
                diversityList[index].startDate === '' ||
                new Date(diversityList[index].expDate) < new Date(diversityList[index].startDate) ||
                false,
              errorStartDate:
                new Date(diversityList[index].expDate) < new Date(diversityList[index].startDate) &&
                diversityList[index].startDate !== ''
                  ? this.props.languageStrings.diversity.errorDates
                  : this.props.languageStrings.diversity.errorRequiredField,
              isErrorExpDate:
                diversityList[index].expDate === '' ||
                new Date(diversityList[index].startDate) > new Date(diversityList[index].expDate) ||
                false,
              errorExpDate:
                new Date(diversityList[index].startDate) > new Date(diversityList[index].expDate) &&
                diversityList[index].expDate !== ''
                  ? ''
                  : this.props.languageStrings.diversity.errorRequiredField
            },
            ...diversityList.slice(index + 1)
          ]
        }),
        () => this.handleFileSubmit(index, 'put')
      );
    }
  };

  handleChangeField = (event: any, fieldName: string, id?: number) => {
    const index = this.state.diversityList.findIndex(
      (item: IDiversityRow) => item.level1Id === id || item.level2Id === id
    );
    this.setState(
      ({ diversityList }) => ({
        diversityList: [
          ...diversityList.slice(0, index),
          {
            ...diversityList[index],
            [fieldName]: event.target.value
          },
          ...diversityList.slice(index + 1)
        ]
      }),
      () => this.setErrors(index)
    );
  };

  selectFile = (filesList: FileList, id?: number) => {
    const validTypeFile = ['jpg', 'jpeg', 'gif', 'png', 'bmp', 'pdf', 'doc', 'docx'];
    const filteredTypeFile = filesList[0].name.split('.');
    const index = this.state.diversityList.findIndex(
      (item: IDiversityRow) => item.level1Id === id || item.level2Id === id
    );
    if (!validTypeFile.includes(filteredTypeFile[filteredTypeFile.length - 1])) {
      const fileInput: any = document.querySelector(`#file${id}`);
      fileInput.value = null;
      this.setState(({ diversityList }) => ({
        diversityList: [
          ...diversityList.slice(0, index),
          {
            ...diversityList[index],
            isErrorFile: true,
            errorFile: this.props.languageStrings.diversity.errorPermitedTypeFile
          },
          ...diversityList.slice(index + 1)
        ]
      }));
    } else if (!isValidFilename(filteredTypeFile[0])) {
      const fileInput: any = document.querySelector(`#file${id}`);
      fileInput.value = null;
      this.setState(({ diversityList }) => ({
        diversityList: [
          ...diversityList.slice(0, index),
          {
            ...diversityList[index],
            isErrorFile: true,
            errorFile: this.props.languageStrings.diversity.errorForbiddenCharacters
          },
          ...diversityList.slice(index + 1)
        ]
      }));
    } else if (index >= 0) {
      this.setState(
        ({ diversityList }) => ({
          diversityList: [
            ...diversityList.slice(0, index),
            {
              ...diversityList[index],
              file: filesList[0],
              fileName: filesList[0].name,
              isErrorFile: false,
              isFilePushed: false
            },
            ...diversityList.slice(index + 1)
          ]
        }),
        () => this.setErrors(index)
      );
    }
  };

  getFilename = (id?: number) => {
    const index = this.state.diversityList.findIndex(
      (item: IDiversityRow) => item.level1Id === id || item.level2Id === id
    );
    if (index >= 0) {
      return this.state.diversityList[index].fileName;
    }
  };

  clearRow = (id?: number) => {
    const index = this.state.diversityList.findIndex(
      (item: IDiversityRow) => item.level1Id === id || item.level2Id === id
    );
    if (
      (this.state.diversityList[index].ISCPUC === 'Y' || !this.state.diversityList[index].isErrorCertificate) &&
      !this.state.diversityList[index].isErrorStartDate &&
      !this.state.diversityList[index].isErrorExpDate
    ) {
      this.getSignedUrl(this.state.diversityList[index], index, 'delete');
    }
    this.clearInputs(index, this.handleFileSubmit);
  };

  expandSecondLevel = (event: MouseEvent<HTMLElement, MouseEvent>, id?: number, value?: boolean): void | undefined => {
    const ev = {
      target: {
        value
      }
    };
    this.handleChangeField(ev, 'isExpanded', id);
    const filteredIds = this.state.diversityList.filter((element: IDiversityRow) => element.level1Id === id);
    for (const id of filteredIds) {
      this.handleChangeField(ev, 'isExpanded', id.level2Id as number);
    }
  };

  submitInfo = (diversityList: IDiversityRow[], forward: boolean = true) => {
    const arrayToSubmit: IDiversitySubmitState = {
      uploadCertificates: diversityList.map((item: IDiversityRow, key: number) => ({
        index: key,
        level1Id: item.level1Id,
        level2Id: item.level2Id,
        classification: item.classification,
        originalFileName: item.fileName,
        document: item.document,
        startDate: item.startDate,
        endDate: item.expDate,
        isErrorFile: item.isErrorFile,
        isErrorCertificate: item.isErrorCertificate,
        isErrorStartDate: item.isErrorStartDate,
        isErrorExpDate: item.isErrorExpDate,
        errorFile: item.errorFile,
        errorCertificate: item.errorCertificate,
        errorStartDate: item.errorStartDate,
        errorExpDate: item.errorExpDate,
        ISCPUC: item.ISCPUC,
        signedUrl: item.signedUrl ? item.signedUrl : '',
        tmpFilePath: item.signedUrl ? item.signedUrl : '',
        referenceId: item.referenceId ? item.referenceId : item.document ? item.document : ''
      }))
    };
    const filteredArray: IRowDiversityState[] = arrayToSubmit.uploadCertificates.filter(
      (element: IRowDiversityState) => {
        if (
          element.originalFileName !== '' ||
          element.referenceId !== '' ||
          element.startDate !== '' ||
          element.endDate !== ''
        ) {
          return true;
        }
        return false;
      }
    );
    const eventMapped = { target: { value: filteredArray } };
    const eventRadio = { target: { value: this.state.radioGroupValue } };
    const continueEnabledEvent = { target: { value: this.state.isContinueEnabled } };
      this.props.handleChange && this.props.handleChange('diversity', 'uploadCertificates')(eventMapped);
      this.props.handleChange && this.props.handleChange('diversity', 'radioGroupValue')(eventRadio);
      this.props.handleChange &&
      this.props.handleChange('diversity', 'isContinueEnabled', () => {
        // Next step triggered in a callback in case the diversity is the last step of the registration process.
        if(forward) 
          this.props.nextStep();
      })(continueEnabledEvent);
  };

  nextStep = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    this.props.handleChange && this.submitInfo(this.state.diversityList);
  };

  previousStep = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    this.props.previousStep();
    this.props.handleChange && this.submitInfo(this.state.diversityList, false);
  };

  renderTaggedMessage(
    isAlreadyTagged: boolean = false,
    taggedDate: string = '',
    uploadCertificates: IRowDiversityState[]
  ) {
    if (isAlreadyTagged && taggedDate.trim().length > 0 && uploadCertificates.length > 0) {
      return (
        <div className="taggedMessage" style={{ marginTop: 0, marginBottom: '16px' }}>
          <Alert severity="info">
            {this.props.languageStrings.formatString(this.props.languageStrings.common.tagInstructionPrimary, {
              taggedDate
            })}
            .&nbsp;
            <strong>
              {this.props.languageStrings.common.tagInstructionSecondary}&nbsp;
              <span>{this.props.languageStrings.continueButtonLabel}.</span>
            </strong>
          </Alert>
        </div>
      );
    } else {
      return '';
    }
  }

  markDownLinkRender(markDownLink: any) {
    return (
      <a href={markDownLink.href} target="_blank" rel="noreferrer">
        {markDownLink.children}
      </a> 
    );
  }

  render() {
    const { loading, isErrorDiversityList, errorDiversityList } = this.state;
    if (loading) {
      return (
        <div style={{ overflow: 'hidden' }}>
          <CircularProgress style={{ width: '24px', height: '24px' }} />
        </div>
      );
    } else {
      return (
        <div className="diversity">
          {isErrorDiversityList ? (
            <Typography align="center" className="note" color="error">
              {errorDiversityList}
            </Typography>
          ) : (
            <>
              <Grid container justifyContent="center">
                <Grid item md={9}>
                  <div
                    className="custom-instructions"                    
                  >
                    <ReactMarkDown children={this.state.diversityInstructions} components={{a: this.markDownLinkRender}} />
                  </div>
                </Grid>
              </Grid>
              <FormControl>
                <Grid container alignItems="center" spacing={2}>
                  <Grid item>
                    <Typography>{this.props.languageStrings.diversity.radioGroupLabel}</Typography>
                  </Grid>
                  <Grid item>
                    <RadioGroup
                      row
                      data-testid="radio-diversity"
                      value={this.state.radioGroupValue}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        this.setState({ radioGroupValue: (event.target as HTMLInputElement).value });
                      }}
                    >
                      <FormControlLabel
                        value="yes"
                        control={<Radio />}
                        label={this.props.languageStrings.affirmativeLabel}
                      />
                      <FormControlLabel
                        value="no"
                        control={<Radio />}
                        label={this.props.languageStrings.negativeLabel}
                      />
                    </RadioGroup>
                  </Grid>
                </Grid>
              </FormControl>
              {this.state.radioGroupValue === 'yes' && (
                <Grid container justifyContent="center">
                  {this.renderTaggedMessage(
                    this.props.isAlreadyTagged,
                    this.props.taggedDate,
                    this.props.initialState.uploadCertificates
                  )}
                  <Grid item md={8}>
                    <TableContainer className="diversity-table">
                      <Table stickyHeader aria-label="Diversity Table">
                        <TableHead>
                          <TableRow>
                            {this.props.languageStrings.diversity.tableColumns.map((column: Column) => (
                              <TableCellStyled key={column.id} align={column.align}>
                                {column.label}
                              </TableCellStyled>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {this.state.diversityList.map((item: IDiversityRow) => (
                            <>
                              {(item.level2Id && !item.isExpanded) || (
                                <TableRow key={item.level2Id || item.level1Id}>
                                  <TableCellStyled size="medium" key={0}>
                                    <Grid container alignItems="center" spacing={4} justifyContent="flex-start">
                                      {item.hasSecondLevel &&
                                        (!item.isExpanded ? (
                                          <Grid item lg={1} xs={3}>
                                            <i
                                              className="material-icons"
                                              title={this.props.languageStrings.expandIconTooltip}
                                              onClick={(event: any) =>
                                                this.expandSecondLevel(event, item.level1Id, true)
                                              }
                                            >
                                              keyboard_arrow_right
                                            </i>
                                          </Grid>
                                        ) : (
                                          <Grid item lg={1} xs={3}>
                                            <i
                                              className="material-icons"
                                              title={this.props.languageStrings.collapseIconTooltip}
                                              onClick={(event: any) =>
                                                this.expandSecondLevel(event, item.level1Id, false)
                                              }
                                            >
                                              keyboard_arrow_down
                                            </i>
                                          </Grid>
                                        ))}
                                      <Grid item xs={8}>
                                        <TypographyStyled isSecondLevel={item.level2Id ? true : false}>
                                          {item.classification}
                                        </TypographyStyled>
                                      </Grid>
                                    </Grid>
                                  </TableCellStyled>
                                  {item.hasSecondLevel ? (
                                    <>
                                      <TableCellStyled key={1}></TableCellStyled>
                                      <TableCellStyled key={2}></TableCellStyled>
                                      <TableCellStyled key={3}></TableCellStyled>
                                      <TableCellStyled key={4}></TableCellStyled>
                                    </>
                                  ) : (
                                    <>
                                      <TableCellStyled align="center" key={1}>
                                        <>
                                          {item.fileName?.length > 0 || (
                                            <label htmlFor={`file${item.level2Id || item.level1Id}`}>
                                              <i
                                                className="material-icons"
                                                title={this.props.languageStrings.diversity.permitedTypeFileTooltip}
                                              >
                                                upload
                                              </i>
                                            </label>
                                          )}
                                          <Grid container alignItems="center" justifyContent="center">
                                            {item.fileName?.length > 0 && (
                                              <>
                                                <label>
                                                  <i
                                                    className="material-icons"
                                                    title="Remove File"
                                                    onClick={() =>
                                                      this.setState({
                                                        isModalOpened: true,
                                                        readyToDelete: item.level2Id || item.level1Id
                                                      })
                                                    }
                                                  >
                                                    clear
                                                  </i>
                                                </label>
                                                <label
                                                  id={`fileName${item.level2Id || item.level1Id}`}
                                                  style={{ marginLeft: 10 }}
                                                >
                                                  {item.fileName.length > 22
                                                    ? `${item.fileName.substr(0, 10)}...${item.fileName.substr(
                                                        item.fileName.length - 7,
                                                        item.fileName.length
                                                      )}`
                                                    : item.fileName}
                                                </label>
                                              </>
                                            )}
                                            {item.isErrorFile && (
                                              <p style={{ color: 'red', padding: '8', fontSize: '12px' }}>
                                                {item.errorFile}
                                              </p>
                                            )}
                                            <TextFieldStyled
                                              id={`file${item.level2Id || item.level1Id}`}
                                              type="file"
                                              onChange={(event: any) => {
                                                this.selectFile(event.target.files, item.level2Id || item.level1Id);
                                              }}
                                            />
                                          </Grid>
                                        </>
                                      </TableCellStyled>
                                      <TableCellStyled align="center" key={2}>
                                        <TextFieldStyled
                                          error={item.isErrorCertificate}
                                          helperText={item.isErrorCertificate && item.errorCertificate}
                                          id={`certificate${item.level2Id || item.level1Id}`}
                                          type="text"
                                          variant="outlined"
                                          value={item.referenceId}
                                          disabled={item.ISCPUC === 'N' ? true : false}
                                          onInput={e =>
                                            this.handleChangeField(e, 'referenceId', item.level2Id || item.level1Id)
                                          }
                                        />
                                      </TableCellStyled>
                                      <TableCellStyled align="center" key={3}>
                                        <TextFieldStyled
                                          error={item.isErrorStartDate}
                                          helperText={item.isErrorStartDate && item.errorStartDate}
                                          id={`startDate${item.level2Id || item.level1Id}`}
                                          type="date"
                                          value={item.startDate}
                                          onChange={e =>
                                            this.handleChangeField(e, 'startDate', item.level2Id || item.level1Id)
                                          }
                                        />
                                      </TableCellStyled>
                                      <TableCellStyled align="center" key={4}>
                                        <TextFieldStyled
                                          error={item.isErrorExpDate}
                                          helperText={item.isErrorExpDate && item.errorExpDate}
                                          id={`expDate${item.level2Id || item.level1Id}`}
                                          type="date"
                                          value={item.expDate}
                                          onChange={e =>
                                            this.handleChangeField(e, 'expDate', item.level2Id || item.level1Id)
                                          }
                                        />
                                      </TableCellStyled>
                                    </>
                                  )}
                                </TableRow>
                              )}
                            </>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Dialog open={this.state.isModalOpened} onClose={() => this.setState({ isModalOpened: false })}>
                      <DialogContent>
                        <Typography align="center">
                          {this.props.languageStrings.formatString(this.props.languageStrings.diversity.modalText, {
                            fileName: this.getFilename(this.state.readyToDelete) as string
                          })}
                        </Typography>
                      </DialogContent>
                      <DialogActions>
                        <Grid container direction="row" justifyContent="space-evenly">
                          <Button onClick={() => this.setState({ isModalOpened: false })} color="primary">
                            {this.props.languageStrings.diversity.modalCancelButton}
                          </Button>
                          <Button
                            onClick={() => this.clearRow(this.state.readyToDelete)}
                            variant="contained"
                            color="primary"
                          >
                            {this.props.languageStrings.diversity.modalDeleteButton}
                          </Button>
                        </Grid>
                      </DialogActions>
                    </Dialog>
                  </Grid>
                </Grid>
              )}
              <br />
              <br />
              <br />
              <Grid container justifyContent="center">
                <Grid item md={9}>
                  <Typography align="center" className="note" color="primary">
                    {this.props.languageStrings.diversity.note}
                  </Typography>
                </Grid>
              </Grid>
            </>
          )}
          <div className="Form-actions multiple">
            <Button
              disabled={!this.state.isContinueEnabled}
              variant="outlined"
              color="primary"
              onClick={e => {
                this.previousStep(e);
              }}
            >
              {this.props.languageStrings.backButtonLabel}
            </Button>
            <Button
              disabled={!this.state.isContinueEnabled}
              variant="contained"
              color="primary"
              onClick={e => {
                this.nextStep(e);
              }}
            >
              {this.props.languageStrings.continueButtonLabel}
            </Button>
          </div>
        </div>
      );
    }
  }
}

export default Diversity;
